import './assets/tailwind.css';
import './assets/fonts.css';
import '@fortawesome/fontawesome-free/css/fontawesome.css';
import '@fortawesome/fontawesome-free/css/solid.css';
import { createInertiaApp, router } from '@inertiajs/react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { resolvePage } from './utils/resolvePage';
import { dropForeignHistoryState, guardForeignPopstate } from './utils/historyState';
import { trackPageView } from './utils/tracking';

// Pageviews de la SPA (#95): el listener vive acá y no en MainLayout para que
// TODA página emita page_view al navegar, use el layout que use. GA/Meta no
// ven las navegaciones de Inertia sin esto y los números no cierran contra
// los clicks que reportan las plataformas.
router.on('navigate', (event) => {
    trackPageView(event.detail.page.url);
});

// Prevent transient network errors during Inertia visits from becoming
// unhandled promise rejections reported to Sentry (e.g. user goes offline
// mid-navigation). These are not actionable and only add noise.
router.on('exception', (event) => {
    const error = event.detail.exception;
    if (error?.name === 'AxiosError' && error?.message === 'Network Error') {
        event.preventDefault();
    }
});

// Must run before createInertiaApp(): Inertia inspects history.state during
// init() to decide whether this is a back/forward visit, and a state written by
// non-Inertia code makes it resolve a component named `undefined`.
dropForeignHistoryState(window.history, window.location.href);

// Same problem reached through back/forward after boot: swallow the popstate
// before Inertia resolves a component named `undefined`, and reload the URL we
// landed on as a regular visit. `replace` rewrites the broken history entry
// with a real Inertia page state so the next back/forward is clean.
guardForeignPopstate(window, () => {
    router.visit(window.location.href, { replace: true, preserveState: false, preserveScroll: false });
});

createInertiaApp({
    resolve: (name) => {
        const pages = import.meta.glob('./pages/**/*.tsx', { eager: true }) as Record<string, any>;
        return resolvePage(pages, name);
    },
    setup({ el, App, props }) {
        if (import.meta.env.PROD && el.innerHTML) {
            hydrateRoot(el, <App {...props} />);
        } else {
            createRoot(el).render(<App {...props} />);
        }
    },
});
